/** @jsxImportSource @emotion/react */
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { paragraphMedium, weightSemiBold } from 'src/components/css'
import { Alert, Modal } from 'antd'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import DisplayedStandingsInput from 'src/components/ts-components/DisplayedStandingsInput'
import { TextAreaInput } from 'src/components/ts-components/TextAreaInput'
import Button from 'src/components/ts-components/Button'
import { Separator } from '../GeneralForm'
import { useScheduleContext } from '../ScheduleContext'
import TiebreakerModal from './TiebreakerModal'
import DisplayStandingsModal from './DisplayStandingsModal'
import '../styles.scss'
import MultipleOptionsInput from 'src/components/ts-components/MultipleOptionsInput'
import { getErrorMessage } from '@sportninja/common/utils/utils'

type StatOption = {
  label: React.ReactNode
  value: string
}

const singleDigitRegex = /^[0-9]$/

const ReprocessHiddenButton = ({ ...props }) => {
  return (
    <button
      id='reprocess-hidden-button'
      css={css`
        display: flex;
        padding: 8px 16px 8px 8px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: 8px;
        border: 1px solid ${colors.ERROR_200};
        box-shadow: 0px 2px 0px 0px rgba(5, 145, 255, 0.1);
        /* hide the button */
        display: none;
      `}
      {...props}
    >
      <DeleteOutlined
        css={css`
          color: ${colors.ERROR_200};
        `}
        width={24}
        height={24}
      />
      <p
        css={css`
          ${paragraphMedium}
          ${weightSemiBold}
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
            : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
          color: ${colors.WHITE};
        `}
      >
        DELETE {isCanlan ? 'SEASON' : 'COMPETITION'}
      </p>
    </button>
  )
}

type SettingsFormProps = {
  onCancelPressed: () => void
  triggerRefresh: () => void
  addPending: (id: string | undefined) => void
  isMobile: boolean
}

const SettingsForm: React.FC<SettingsFormProps> = ({
  onCancelPressed,
  triggerRefresh,
  addPending,
  isMobile,
}) => {
  const [isTiebreakerModalOpen, setIsTiebreakerModalOpen] = useState(false)
  const [isDisplayModalOpen, setIsDisplayModalOpen] = useState(false)
  const [isPlayerStatsModalOpen, setIsPlayerStatsModalOpen] = useState(false)
  const [isGoalieStatsModalOpen, setIsGoalieStatsModalOpen] = useState(false)
  // Local state for the new Reprocess modal (drop-in replacement for DeleteModal)
  const [isReprocessOpen, setIsReprocessOpen] = useState(false)
  const [localLoading, setLocalLoading] = useState(false)
  const [reason, setReason] = useState('')
  const [reasonFieldErrors, setReasonFieldErrors] = useState<string[]>([])
  const [reasonCustomError, setReasonCustomError] = useState<any>({})

  const {
    id,
    fieldErrors,
    customError,
    setCustomErroByField,
    clearCustomErrorByField,
    skipTeamOfficialsSignatures,
    setSkipTeamOfficialsSignatures,
    skipOfficialsSignatures,
    setSkipOfficialsSignatures,
    timingPeriodLength,
    setTimingPeriodLength,
    timingExtraPeriodLength,
    setTimingExtraPeriodLength,
    regulationGameLength,
    setRegulationGameLength,
    requireGameOfficials,
    setRequireGameOfficials,
    goalsPerForfeit,
    forfeitLoss,
    wins,
    ties,
    losses,
    overtimerWin,
    overtimeLoss,
    shootoutLoss,
    setGoalsPerForfeit,
    setForfeitLoss,
    setWins,
    setTies,
    setLosses,
    setOvertimeWin,
    setOvertimeLoss,
    setShootoutLoss,
    onSubmit,
    onCancel,
    requireUniqueJerseyNumbers,
    setRequireUniqueJerseyNumbers,
    requireWaiverSignature,
    setRequireWaiverSignature,
    requireTimeGameEvent,
    setRequireTimeGameEvent,
    requireGoalType,
    setRequireGoalType,
    requireGoalConfirmation,
    setRequireGoalConfirmation,
    assignPointsToPlayer,
    setAssignPointsToPlayer,
    defaultSortBy,
    setDefaultSortBy,
    tiebreakerStats,
    setTiebreakerStats,
    displayedStandings,
    setDisplayedStandings,
    statTypes,
    playerStatsTypes,
    fullAllStatTypes,
    isSoccer,
    isGenericSport,
    requireGoalkeeper,
    setRequireGoalkeeper,
    requireRosters,
    setRequireRosters,
    scoringValues,
    setScoringValues,
    allowTeamOfficialScoring,
    setAllowTeamOfficialScoring,
    displayedPlayerStats,
    setDisplayedPlayerStats,
    displayedGoalieStats,
    setDisplayedGoalieStats,
    playerStatsDefaultSortBy,
    setPlayerStatsDefaultSortBy,
    goalieStatsDefaultSortBy,
    setGoalieStatsDefaultSortBy,
    displayLeaderboards,
    setDisplayLeaderboards,
  } = useScheduleContext()

  const [reprocessError, setReprocessError] = useState<string | null>(null)

  const playerDefaultSortLabel = isSoccer
    ? 'Default Player Statistics Sort By'
    : `${
        isGenericSport ? 'Default Player' : 'Default Skater'
      } Statistics Sort By`
  const goalieDefaultSortLabel = isSoccer
    ? 'Default Keeper Statistics Sort By'
    : 'Default Goalie Statistics Sort By'

  const createStatOptions = useCallback(
    (statIds: number[]): StatOption[] => {
      return statIds.reduce<StatOption[]>((acc, statId) => {
        const stat = fullAllStatTypes.find((s) => s.id === statId)
        if (!stat) {
          return acc
        }
        const primaryText = stat.abbreviation || stat.name || ''
        const secondaryText =
          stat.abbreviation && stat.name ? ` (${stat.name})` : ''
        acc.push({
          label: (
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <Text
                variant='paragraphSmall'
                color={colors.WHITE}
                weight='regular'
              >
                {primaryText}
                {secondaryText}
              </Text>
              {stat.description && (
                <Text
                  variant='paragraphXSmall'
                  color={colors.NEUTRAL_50}
                  weight='regular'
                >
                  {stat.description}
                </Text>
              )}
            </div>
          ),
          value: String(stat.id),
        })
        return acc
      }, [])
    },
    [fullAllStatTypes]
  )

  const playerStatOptions = useMemo(
    () => createStatOptions(displayedPlayerStats),
    [createStatOptions, displayedPlayerStats]
  )
  const goalieStatOptions = useMemo(
    () => createStatOptions(displayedGoalieStats),
    [createStatOptions, displayedGoalieStats]
  )
  const isPlayerSortDisabled =
    playerStatsTypes?.length === 0 || displayedPlayerStats.length === 0
  const isGoalieSortDisabled =
    playerStatsTypes?.length === 0 || displayedGoalieStats.length === 0

  // Enforce false values for specific settings when isGenericSport is true
  // And clear values when isGenericSport is false
  useEffect(() => {
    if (isGenericSport) {
      setRequireTimeGameEvent(false)
      setRequireGoalType(false)
      setRequireGoalConfirmation(false)
    } else {
      // Clear scoring values and assign points setting when not generic sport
      setScoringValues([])
      setAssignPointsToPlayer(false)
      // Require rosters is only available for generic sports, default to true for non-generic
      setRequireRosters(true)
    }
  }, [
    isGenericSport,
    setAssignPointsToPlayer,
    setRequireGoalConfirmation,
    setRequireGoalType,
    setRequireRosters,
    setRequireTimeGameEvent,
    setScoringValues,
  ])

  // Cascade disable roster-related settings when rosters are not required
  useEffect(() => {
    if (!requireRosters) {
      // When rosters not required, turn off roster-related validations
      setRequireUniqueJerseyNumbers(false)
      setRequireWaiverSignature(false)
    }
  }, [requireRosters, setRequireUniqueJerseyNumbers, setRequireWaiverSignature])

  // const defaultTiebreakerStats =
  //   defaultStandingsSettingsForSport?.sort_order?.tie_breaker_stats

  // Handle edit tiebreaker click
  const handleEditTiebreaker = () => {
    if (statTypes?.length === 0) {
      return
    }
    setIsTiebreakerModalOpen(true)
  }

  const handleTiebreakerModalClose = () => {
    setIsTiebreakerModalOpen(false)
  }

  const handleTiebreakerModalSave = (selectedStats: number[]) => {
    setTiebreakerStats(selectedStats)
    setIsTiebreakerModalOpen(false)
  }

  // Function to get stat label by value
  const getStatLabel = (value: number) => {
    const name = statTypes.find((option) => option.id === value)?.name
    return name || ''
  }

  // Function to get formatted tiebreaker stats text
  /**
   * Formats the tiebreaker stats text with order indices
   * @returns {string} Formatted string of tiebreaker stats with their order indices
   */
  const getTiebreakerStatsText = () => {
    if (tiebreakerStats.length === 0) {
      return ''
    }
    return tiebreakerStats
      .map((stat, index) => `${getStatLabel(stat)} (${index + 1})`)
      .join(', ')
  }

  // Handle edit display standings click
  const handleEditDisplayStandings = () => {
    if (statTypes?.length === 0) {
      return
    }
    setIsDisplayModalOpen(true)
  }

  const handleDisplayModalClose = () => {
    setIsDisplayModalOpen(false)
  }

  const handleDisplayModalSave = (
    selectedStats: number[],
    isDefaultSortByRemoved: boolean
  ) => {
    setDisplayedStandings(selectedStats)

    // Clear defaultSortBy if it was removed from displayed standings
    if (isDefaultSortByRemoved) {
      setDefaultSortBy(null)
    }

    setIsDisplayModalOpen(false)
  }

  return (
    <div
      css={css`
        padding-top: 16px;
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 16px;
        display: flex;
        flex: 1;
        flex-direction: column;
        overflow-y: auto;
        height: 100%;
        gap: 16px;
        border-bottom: 1px solid #3f4753;
        ${isMobile &&
        css`
          padding-left: 8px;
          padding-right: 8px;
          width: 100%;
        `}
      `}
    >
      {/* Hidden trigger button preserved to maintain programmatic click behavior */}
      <ReprocessHiddenButton onClick={() => setIsReprocessOpen(true)} />

      {/* Drop-in replacement modal using the same content and layout as pages/Schedule/ReprocessStats.jsx */}
      <Modal
        css={css`
          display: flex;
          flex: 1;
          height: ${isMobile ? '100vh' : 'auto'} !important;
          margin: ${isMobile ? '0' : '24px'};
          padding: 0;
          max-width: ${isMobile ? '100%' : '500px'};
          top: ${isMobile ? '0' : '50%'};
          .ant-modal-content {
            height: ${isMobile ? '100vh' : 'auto'} !important;
            border-radius: ${isMobile ? '0' : '16px'};
            display: flex;
            flex-direction: column;
            background: ${colors.SECONDARY_300};
            border: 1px solid ${colors.SECONDARY_100};
          }
          .ant-modal-body {
            flex: 1;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            padding: 24px;
          }
          .ant-modal-header {
            display: none;
          }
        `}
        width={isMobile ? '100%' : 500}
        open={isReprocessOpen}
        onCancel={() => {
          setIsReprocessOpen(false)
          setReasonFieldErrors([])
          setReasonCustomError({})
          setReason('')
          setReprocessError(null)
        }}
        centered={!isMobile}
        maskClosable={false}
        footer={null}
        styles={{
          content: {
            backgroundColor: colors.SECONDARY_400,
            padding: 0,
            borderWidth: 1,
            borderColor: colors.SECONDARY_300,
          },
          wrapper: {
            zIndex: 10002,
          },
          mask: {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(6, 10, 16, 0.8)',
            zIndex: 10001,
          },
        }}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;
            gap: 16px;
            position: relative;
          `}
        >
          {/* Warning Icon */}
          <WarningOutlined
            css={css`
              font-size: 48px;
              color: ${colors.ERROR_200};
            `}
          />

          {/* Title */}
          <Text
            variant='heading3'
            weight='semiBold'
            color={colors.WHITE}
            style={`
              text-align: center;
            `}
          >
            Reprocess Statistics
          </Text>

          {/* Description */}
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WHITE}
            >
              Use this if stats appear incorrect or misaligned. <br />
              It will recalculate all game data and rebuild totals from scratch.
            </Text>
          </div>

          {/* Divider */}
          <div
            css={css`
              align-self: stretch;
              height: 1px;
              background: ${colors.SECONDARY_100};
            `}
          />

          {/* Form section */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-self: stretch;
              gap: 16px;
            `}
          >
            <TextAreaInput
              name='reason'
              value={reason}
              setValue={(v) => setReason(v)}
              fieldErrors={reasonFieldErrors}
              customError={reasonCustomError}
              label='Describe the Reason'
              placeholder='Enter Details'
              maxLength={255}
              disabled={localLoading}
              rows={1}
              autoSize={{ minRows: 1, maxRows: 4 }}
            />
          </div>

          {/* Note box */}
          <div
            css={css`
              align-self: stretch;
              padding: 8px 16px;
              background: ${colors.SECONDARY_300};
              border-radius: 8px;
              border: 1px solid ${colors.SECONDARY_100};
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WHITE}
            >
              <span
                css={css`
                  font-weight: 700;
                `}
              >
                Note:
              </span>{' '}
              Depending On The Number Of Games, This Process May Take Several
              Minutes To Complete. If Issues Persist, Contact{' '}
              <a
                href='mailto:support@sportninja.com'
                target='_blank'
                rel='noopener noreferrer'
                css={css`
                  color: ${colors.PRIMARY};
                  &:hover {
                    opacity: 0.8;
                  }
                `}
              >
                support@sportninja.com
              </a>
            </Text>
          </div>

          {/* Divider */}
          <div
            css={css`
              align-self: stretch;
              height: 1px;
              background: ${colors.SECONDARY_100};
            `}
          />

          {reprocessError && (
            <Alert
              message={reprocessError}
              type='error'
              showIcon
              css={css`
                align-self: stretch;
              `}
            />
          )}

          {/* Action buttons */}
          <div
            css={css`
              align-self: stretch;
              justify-content: center;
              align-items: flex-start;
              gap: 16px;
              display: inline-flex;
            `}
          >
            <Button
              text='CANCEL'
              onClick={() => {
                setIsReprocessOpen(false)
                setReasonFieldErrors([])
                setReasonCustomError({})
                setReason('')
                setReprocessError(null)
              }}
              disabled={localLoading}
              variant='secondary'
            />

            <Button
              text='REPROCESS'
              onClick={async () => {
                // Close modal and begin processing, preserving prior behavior
                setLocalLoading(true)
                setReprocessError(null)
                try {
                  // Save settings before reprocessing (to avoid regressions)
                  await onSubmit()
                  const payloadReason = reason?.trim() || undefined

                  // Build query string with proper URL encoding
                  const params = new URLSearchParams()
                  if (payloadReason) {
                    params.append('reprocess_reason', payloadReason)
                  }
                  const queryString = params.toString()

                  await req(
                    `/schedules/${id}/reprocess${
                      queryString ? '?' + queryString : ''
                    }`
                  )
                  setIsReprocessOpen(false)
                  addPending(id)
                  triggerRefresh()
                  onCancel()
                  onCancelPressed()
                } catch (err) {
                  const errorMessage = getErrorMessage(err)
                  setReprocessError(errorMessage)
                } finally {
                  setLocalLoading(false)
                }
              }}
              disabled={localLoading || !reason || reason?.trim().length === 0}
              isLoading={localLoading}
              variant='danger'
            />
          </div>
        </div>
      </Modal>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          gap: 16px;
        `}
      >
        {/* Timing Settings */}
        {!isGenericSport && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-end;
              gap: 16px;
              align-self: stretch;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: flex-start;
                gap: 8px;
                align-self: stretch;
              `}
            >
              <Text
                variant='captionLarge'
                weight='semiBold'
                color={colors.NEUTRAL_0}
              >
                Timing Settings
              </Text>
            </div>
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;
                ${isMobile &&
                css`
                  width: 100%;
                  flex-direction: column;
                `}
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 8px;
                  flex: 1 0 0;
                  width: 100%;
                `}
              >
                <TextInput
                  name={'regulationGameLength'}
                  value={
                    regulationGameLength !== null
                      ? regulationGameLength.toString()
                      : ''
                  }
                  setValue={(value) =>
                    setRegulationGameLength(value ? parseFloat(value) : null)
                  }
                  fieldErrors={fieldErrors}
                  customError={customError}
                  label='Regulation Game Length (mins)'
                  type='number'
                />
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 8px;
                  flex: 1 0 0;
                  width: 100%;
                `}
              >
                <TextInput
                  name={'timingPeriodLength'}
                  value={
                    timingPeriodLength !== null
                      ? timingPeriodLength.toString()
                      : ''
                  }
                  setValue={(value) =>
                    setTimingPeriodLength(value ? parseFloat(value) : null)
                  }
                  fieldErrors={fieldErrors}
                  customError={customError}
                  label='Default Period Length (mins)'
                  type='number'
                />
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 8px;
                  flex: 1 0 0;
                  width: 100%;
                `}
              >
                <TextInput
                  name={'timingExtraPeriodLength'}
                  value={
                    timingExtraPeriodLength !== null
                      ? timingExtraPeriodLength.toString()
                      : ''
                  }
                  setValue={(value) =>
                    setTimingExtraPeriodLength(value ? parseFloat(value) : null)
                  }
                  fieldErrors={fieldErrors}
                  customError={customError}
                  label='Default Overtime Length (mins)'
                  type='number'
                />
              </div>
            </div>
          </div>
        )}
        {!isGenericSport && <Separator />}
        {/* Point System */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text
              variant='captionLarge'
              weight='semiBold'
              color={colors.NEUTRAL_0}
            >
              Point System
            </Text>
          </div>
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              gap: 16px;
              align-self: stretch;
              ${isMobile &&
              css`
                width: 100%;
                flex-direction: column;
              `}
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'wins'}
                value={wins !== null ? wins.toString() : ''}
                setValue={(value) => setWins(value ? parseFloat(value) : null)}
                fieldErrors={fieldErrors}
                customError={customError}
                label='Wins'
                type='number'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'ties'}
                value={ties !== null ? ties.toString() : ''}
                setValue={(value) => setTies(value ? parseFloat(value) : null)}
                fieldErrors={fieldErrors}
                customError={customError}
                label='Ties'
                type='number'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'losses'}
                value={losses !== null ? losses.toString() : ''}
                setValue={(value) =>
                  setLosses(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Losses'
                type='number'
              />
            </div>
          </div>
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              gap: 16px;
              align-self: stretch;
              ${isMobile &&
              css`
                width: 100%;
                flex-direction: column;
              `}
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'overtimerWin'}
                value={overtimerWin !== null ? overtimerWin.toString() : ''}
                setValue={(value) =>
                  setOvertimeWin(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Overtime Win'
                type='number'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'overtimeLoss'}
                value={overtimeLoss !== null ? overtimeLoss.toString() : ''}
                setValue={(value) =>
                  setOvertimeLoss(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Overtime Loss'
                type='number'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'shootoutLoss'}
                value={shootoutLoss !== null ? shootoutLoss.toString() : ''}
                setValue={(value) =>
                  setShootoutLoss(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Shootout Loss'
                type='number'
              />
            </div>
          </div>
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              gap: 16px;
              align-self: stretch;
              ${isMobile &&
              css`
                width: 100%;
                flex-direction: column;
              `}
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'forfeitLoss'}
                value={forfeitLoss !== null ? forfeitLoss.toString() : ''}
                setValue={(value) =>
                  setForfeitLoss(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Forfeit Penalty (PTS Deducted)'
                type='number'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <TextInput
                name={'goalsPerForfeit'}
                value={
                  goalsPerForfeit !== null ? goalsPerForfeit.toString() : ''
                }
                setValue={(value) =>
                  setGoalsPerForfeit(value ? parseFloat(value) : null)
                }
                fieldErrors={fieldErrors}
                customError={customError}
                label='Forfeit (Goals Awarded)'
                type='number'
              />
            </div>
          </div>
        </div>
        <Separator />
        {/* Game Setup Settings */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text
              variant='captionLarge'
              weight='semiBold'
              color={colors.NEUTRAL_0}
            >
              Game Setup Settings
            </Text>
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Skip Team Official Setup'
              subtitle='This Option Allows You To Skip The Setup Of Team Officials.'
              value={skipTeamOfficialsSignatures}
              onChange={setSkipTeamOfficialsSignatures}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Require Game Officials'
              subtitle='This Option Makes At Least One Game Official Required For All Games.'
              value={requireGameOfficials}
              onChange={setRequireGameOfficials}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Skip Game Officials Signatures'
              subtitle='This Option Allows You To Skip The Signatures Of Officials.'
              value={skipOfficialsSignatures}
              onChange={setSkipOfficialsSignatures}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              ${!isGenericSport &&
              css`
                opacity: 0.5;
              `}
            `}
          >
            <SwitchInput
              title='Require Game Roster'
              subtitle='This Option Requires Rosters For Both Teams Before Scoring A Game.'
              value={isGenericSport ? requireRosters : true}
              onChange={(value) => isGenericSport && setRequireRosters(value)}
              disabled={!isGenericSport}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              ${!requireRosters &&
              css`
                opacity: 0.5;
              `}
            `}
          >
            <SwitchInput
              title='Require Unique Jersey Numbers'
              subtitle='This Option Will Enforce All Players Have A Unique Jersey # When Setting Up A Game.'
              value={requireRosters ? requireUniqueJerseyNumbers : false}
              onChange={(value) =>
                requireRosters && setRequireUniqueJerseyNumbers(value)
              }
              disabled={!requireRosters}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              ${!requireRosters &&
              css`
                opacity: 0.5;
              `}
            `}
          >
            <SwitchInput
              title='Require Waiver Signature Before Player Can Play'
              subtitle='This Option Will Requires Players To Sign The Waiver Before They Can Be Added To A Game.'
              value={requireRosters ? requireWaiverSignature : false}
              onChange={(value) =>
                requireRosters && setRequireWaiverSignature(value)
              }
              disabled={!requireRosters}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              ${isGenericSport &&
              css`
                opacity: 0.5;
              `}
            `}
          >
            <SwitchInput
              title={`Require ${isSoccer ? 'Goalkeeper' : 'Goalie'}`}
              subtitle={`This Option Makes Having At Least One ${
                isSoccer ? 'Goalkeeper' : 'Goalie'
              } Required For All Games.`}
              value={isGenericSport ? false : requireGoalkeeper}
              onChange={setRequireGoalkeeper}
              disabled={isGenericSport}
            />
          </div>
          {/* For now, this is being disabled */}
          {/* <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Skip Starting Line-up Selection'
              subtitle='This option allows you to skip the starting line-up.'
              value={skipStartingLineupSelection}
              onChange={setSkipStartingLineupSelection}
            />
          </div> */}
        </div>
        <Separator />
        {/* Standings Settings */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text
              variant='captionLarge'
              weight='semiBold'
              color={colors.NEUTRAL_0}
            >
              Team Statistics (Standings) Settings
            </Text>
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 16px;
              align-self: stretch;
              ${isMobile &&
              css`
                width: 100%;
                flex-direction: column;
              `}
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              {/* Displayed Standings (read-only) */}
              <div
                onClick={
                  statTypes?.length === 0
                    ? undefined
                    : handleEditDisplayStandings
                }
                css={css`
                  width: 100%;
                  cursor: ${statTypes?.length === 0
                    ? 'not-allowed'
                    : 'pointer'};
                  position: relative;
                  margin-bottom: 16px;
                  ${statTypes?.length === 0 &&
                  css`
                    opacity: 0.5;
                  `}
                `}
              >
                <DisplayedStandingsInput
                  label='Displayed Standings'
                  options={displayedStandings.map((statId) => {
                    const stat = statTypes.find((s) => s.id === statId)
                    return {
                      label: stat?.name || '',
                      value: String(statId),
                    }
                  })}
                  value={displayedStandings.map(String)}
                  onChange={() => {
                    /* no-op, read-only */
                  }}
                  fieldErrors={[]}
                  customError={''}
                  name='displayedStandings'
                />
                {/* invisible overlay keeps the whole control clickable */}
                <div
                  css={css`
                    position: absolute;
                    inset: 0;
                    z-index: 1;
                  `}
                />
              </div>
              <SelectInput
                style={css`
                  height: 50px !important;
                  ${statTypes?.length === 0 &&
                  css`
                    opacity: 0.5;
                  `}
                `}
                name='defaultSortBy'
                value={defaultSortBy ? String(defaultSortBy) : ''}
                onChange={(value) => setDefaultSortBy(parseInt(value, 10))}
                label='Default Sort By'
                options={statTypes
                  .filter((stat) => displayedStandings.includes(stat.id))
                  .map((stat) => ({
                    ...stat,
                    // Replace IN-DIV with DIV for sorting purposes
                    sortKey:
                      stat.abbreviation === 'IN-DIV'
                        ? 'DIV'
                        : stat.abbreviation,
                  }))
                  .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
                  .map((stat) => ({
                    label: (
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                        `}
                      >
                        <Text
                          variant='paragraphSmall'
                          color={colors.WHITE}
                          weight='regular'
                        >
                          {stat.abbreviation === 'IN-DIV'
                            ? 'DIV'
                            : stat.abbreviation}{' '}
                          ({stat.name})
                        </Text>
                        <Text
                          variant='paragraphXSmall'
                          color={colors.NEUTRAL_50}
                          weight='regular'
                        >
                          {stat.description}
                        </Text>
                      </div>
                    ),
                    value: String(stat.id),
                  }))}
                fieldErrors={fieldErrors}
                customError={customError}
                filterOption={(input, option) => {
                  const statName =
                    option.label.props.children[0]?.props?.children[0]
                  const abbreviation =
                    option.label.props.children[0]?.props?.children[2]
                  if (!input || !statName || !abbreviation) {
                    return false
                  }
                  return (
                    statName
                      .toString()
                      .toLowerCase()
                      .includes(input?.toLowerCase() || '') ||
                    abbreviation
                      .toString()
                      .toLowerCase()
                      .includes(input?.toLowerCase() || '')
                  )
                }}
                showSearch={true}
                disabled={statTypes?.length === 0}
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: flex-start;
                  justify-content: space-between;
                  gap: 8px;
                  width: 100%;
                `}
              >
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Secondary Sorting Order: {getTiebreakerStatsText()}
                </Text>
                <button
                  onClick={handleEditTiebreaker}
                  disabled={statTypes?.length === 0}
                  css={css`
                    cursor: pointer;
                    ${statTypes?.length === 0 &&
                    css`
                      opacity: 0.5;
                    `}
                    ${statTypes?.length === 0 &&
                    css`
                      cursor: not-allowed;
                    `}
                  `}
                >
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.PRIMARY}
                  >
                    Customize Secondary Sorting Rules
                  </Text>
                </button>
              </div>
            </div>
            {/* <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 8px;
                  width: 100%;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 8px;
                    flex: 1 0 0;
                    width: 100%;
                  `}
                >
                  <SelectInput
                    name='tiebreakerMode'
                    value={getTiebreakerMode()}
                    onChange={handleTiebreakerModeChange}
                    label='Tiebreaker Logic'
                    options={[
                      { label: 'Default', value: 'default' },
                      { label: 'Custom', value: 'custom' },
                    ]}
                    fieldErrors={fieldErrors}
                    customError={customError}
                    disabled={false}
                  />
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    {getTiebreakerStatsText()}
                  </Text>
                </div>
                {getTiebreakerMode() === 'custom' && (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 4px;
                      cursor: pointer;
                      border: 1px solid ${colors.PRIMARY};
                      border-radius: 8px;
                      padding: 4px 8px;
                      align-self: center;
                    `}
                    onClick={handleEditTiebreaker}
                  >
                    <Text
                      variant='paragraphMedium'
                      weight='semiBold'
                      color={colors.PRIMARY}
                    >
                      Edit
                    </Text>
                    <EditOutlined
                      css={css`
                        color: ${colors.PRIMARY};
                        font-size: 20px;
                      `}
                    />
                  </div>
                )}
              </div>
            </div> */}
          </div>
        </div>
        <Separator />
        {/* Statistics Settings */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text
              variant='captionLarge'
              weight='semiBold'
              color={colors.NEUTRAL_0}
            >
              {isGenericSport || isSoccer ? 'Player' : 'Skater'} Statistics
              Settings
            </Text>
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 16px;
              align-self: stretch;
              ${isMobile &&
              css`
                width: 100%;
                flex-direction: column;
              `}
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                width: 100%;
              `}
            >
              {/* Displayed Player Statistics (read-only) */}
              <div
                onClick={
                  playerStatsTypes?.length === 0
                    ? undefined
                    : () => setIsPlayerStatsModalOpen(true)
                }
                css={css`
                  width: 100%;
                  cursor: ${playerStatsTypes?.length === 0
                    ? 'not-allowed'
                    : 'pointer'};
                  position: relative;
                  margin-bottom: 16px;
                  ${playerStatsTypes?.length === 0 &&
                  css`
                    opacity: 0.5;
                  `}
                `}
              >
                <DisplayedStandingsInput
                  label={
                    isSoccer
                      ? 'Displayed Player Statistics'
                      : `${
                          isGenericSport
                            ? 'Displayed Player'
                            : 'Displayed Skater'
                        } Statistics`
                  }
                  options={displayedPlayerStats.map((statId) => {
                    const stat = fullAllStatTypes.find((s) => s.id === statId)
                    return {
                      label: stat?.name || '',
                      value: String(statId),
                    }
                  })}
                  value={displayedPlayerStats.map(String)}
                  onChange={() => {
                    /* no-op, read-only */
                  }}
                  fieldErrors={[]}
                  customError={''}
                  name='displayedPlayerStats'
                />
                {/* invisible overlay keeps the whole control clickable */}
                <div
                  css={css`
                    position: absolute;
                    inset: 0;
                    z-index: 1;
                  `}
                />
              </div>
              <SelectInput
                style={css`
                  width: 100%;
                  height: 50px !important;
                  ${isPlayerSortDisabled &&
                  css`
                    opacity: 0.5;
                  `}
                `}
                name='playerStatsDefaultSortBy'
                value={
                  playerStatsDefaultSortBy !== null
                    ? String(playerStatsDefaultSortBy)
                    : ''
                }
                onChange={(value) =>
                  setPlayerStatsDefaultSortBy(
                    value ? parseInt(value, 10) : null
                  )
                }
                label={playerDefaultSortLabel}
                options={playerStatOptions}
                fieldErrors={fieldErrors}
                customError={customError}
                disabled={isPlayerSortDisabled}
                showSearch={true}
              />
              {!isGenericSport && (
                <>
                  <div
                    onClick={
                      playerStatsTypes?.length === 0
                        ? undefined
                        : () => setIsGoalieStatsModalOpen(true)
                    }
                    css={css`
                      width: 100%;
                      cursor: ${playerStatsTypes?.length === 0
                        ? 'not-allowed'
                        : 'pointer'};
                      position: relative;
                      ${playerStatsTypes?.length === 0 &&
                      css`
                        opacity: 0.5;
                      `}
                    `}
                  >
                    <DisplayedStandingsInput
                      label={
                        isSoccer
                          ? 'Displayed Keeper Statistics'
                          : 'Displayed Goalie Statistics'
                      }
                      options={displayedGoalieStats.map((statId) => {
                        const stat = fullAllStatTypes.find(
                          (s) => s.id === statId
                        )
                        return {
                          label: stat?.name || '',
                          value: String(statId),
                        }
                      })}
                      value={displayedGoalieStats.map(String)}
                      onChange={() => {
                        /* no-op, read-only */
                      }}
                      fieldErrors={[]}
                      customError={''}
                      name='displayedGoalieStats'
                    />
                    {/* invisible overlay keeps the whole control clickable */}
                    <div
                      css={css`
                        position: absolute;
                        inset: 0;
                        z-index: 1;
                      `}
                    />
                  </div>
                  <SelectInput
                    style={css`
                      width: 100%;
                      height: 50px !important;
                      ${isGoalieSortDisabled &&
                      css`
                        opacity: 0.5;
                      `}
                    `}
                    name='goalieStatsDefaultSortBy'
                    value={
                      goalieStatsDefaultSortBy !== null
                        ? String(goalieStatsDefaultSortBy)
                        : ''
                    }
                    onChange={(value) =>
                      setGoalieStatsDefaultSortBy(
                        value ? parseInt(value, 10) : null
                      )
                    }
                    label={goalieDefaultSortLabel}
                    options={goalieStatOptions}
                    fieldErrors={fieldErrors}
                    customError={customError}
                    disabled={isGoalieSortDisabled}
                    showSearch={true}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        <Separator />
        {/* Leaderboards Settings */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text
              variant='captionLarge'
              weight='semiBold'
              color={colors.NEUTRAL_0}
            >
              Leaderboards Settings
            </Text>
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Display Leaderboards'
              subtitle='Enable this option to display the leaderboards section on the statistics tab.'
              value={displayLeaderboards}
              onChange={setDisplayLeaderboards}
            />
          </div>
        </div>
        <Separator />
        {/* Scoring Settings */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text
              variant='captionLarge'
              weight='semiBold'
              color={colors.NEUTRAL_0}
            >
              Scoring Settings
            </Text>
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              ${isGenericSport &&
              css`
                opacity: 0.5;
              `}
            `}
          >
            <SwitchInput
              title='Require Time of Events'
              subtitle='Require the time of events to be set for all games.'
              value={isGenericSport ? false : requireTimeGameEvent}
              onChange={(value) =>
                !isGenericSport && setRequireTimeGameEvent(value)
              }
              disabled={isGenericSport}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              ${isGenericSport &&
              css`
                opacity: 0.5;
              `}
            `}
          >
            <SwitchInput
              title='Require Goal Type'
              subtitle='Require the goal type to be set for all goals.'
              value={isGenericSport ? false : requireGoalType}
              onChange={(value) => !isGenericSport && setRequireGoalType(value)}
              disabled={isGenericSport}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              ${isGenericSport &&
              css`
                opacity: 0.5;
              `}
            `}
          >
            <SwitchInput
              title='Require Goal Confirmation Screen'
              subtitle='Display the confirmation screen after a event is recorded.'
              value={isGenericSport ? false : requireGoalConfirmation}
              onChange={(value) =>
                !isGenericSport && setRequireGoalConfirmation(value)
              }
              disabled={isGenericSport}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              ${!isGenericSport &&
              css`
                opacity: 0.5;
              `}
              ${isGenericSport &&
              css`
                display: flex;
              `}
            `}
          >
            <SwitchInput
              title='Assign Points to Player'
              subtitle='When enabled, points are assigned to the player who scored. When disabled, points are assigned to the team.'
              value={isGenericSport ? assignPointsToPlayer : false}
              onChange={(value) =>
                isGenericSport && setAssignPointsToPlayer(value)
              }
              disabled={!isGenericSport}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <SwitchInput
              title='Allow Team Officials to Score'
              subtitle='This Option Allows Team Officials To Score Games.'
              value={allowTeamOfficialScoring}
              onChange={setAllowTeamOfficialScoring}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: flex-start;
              gap: 16px;
              align-self: stretch;
              width: 100%;
              ${!isGenericSport &&
              css`
                opacity: 0.5;
              `}
            `}
          >
            <MultipleOptionsInput
              label='Set Scoring Values'
              tip='The first value listed here will be used by default when scoring. Others appear on long press.'
              value={isGenericSport ? scoringValues.map(String) : []}
              onChange={(value) => {
                if (!isGenericSport) {
                  return
                }
                // clear the error
                clearCustomErrorByField('scoringValues')
                if (value.length > 4) {
                  setCustomErroByField(
                    'scoringValues',
                    'You Can Enter At Most 4 Values.'
                  )
                  return
                }
                // Check for single digit values (0-9)
                const hasInvalidDigits = value.some(
                  (v) => !singleDigitRegex.test(v)
                )
                if (hasInvalidDigits) {
                  setCustomErroByField(
                    'scoringValues',
                    'Scoring Values Must Be Single Digits (0-9).'
                  )
                  return
                }
                // Check for duplicate values within the new array
                const hasDuplicates = value.some(
                  (v, index) => value.indexOf(v) !== index
                )
                if (hasDuplicates) {
                  setCustomErroByField(
                    'scoringValues',
                    'The Value Is Already In The List.'
                  )
                  return
                }
                clearCustomErrorByField('scoringValues')
                setScoringValues(value.map(Number))
              }}
              placeholder='Enter Scoring Point Values'
              type='number'
              validator={singleDigitRegex}
              fieldErrors={fieldErrors}
              customError={customError}
              name='scoringValues'
              disabled={!isGenericSport}
            />
          </div>
        </div>
      </div>
      <TiebreakerModal
        isOpen={isTiebreakerModalOpen}
        onClose={handleTiebreakerModalClose}
        onSave={handleTiebreakerModalSave}
        currentStats={tiebreakerStats}
        statTypes={statTypes}
        defaultSortBy={defaultSortBy}
      />
      <DisplayStandingsModal
        isOpen={isDisplayModalOpen}
        onClose={handleDisplayModalClose}
        onSave={handleDisplayModalSave}
        currentDisplayed={displayedStandings}
        statTypes={statTypes?.filter((statType) => !statType.is_h2h)}
        defaultSortBy={defaultSortBy}
      />
      <DisplayStandingsModal
        isOpen={isPlayerStatsModalOpen}
        onClose={() => setIsPlayerStatsModalOpen(false)}
        onSave={(selectedStats) => {
          setDisplayedPlayerStats(selectedStats)
          if (
            playerStatsDefaultSortBy !== null &&
            !selectedStats.includes(playerStatsDefaultSortBy)
          ) {
            setPlayerStatsDefaultSortBy(null)
          }
          setIsPlayerStatsModalOpen(false)
        }}
        currentDisplayed={displayedPlayerStats}
        statTypes={playerStatsTypes}
        defaultSortBy={null}
        title={
          isSoccer
            ? 'Customize Displayed Player Statistics'
            : `${
                isGenericSport
                  ? 'Customize Displayed Player'
                  : 'Customize Displayed Skater'
              } Statistics`
        }
      />
      {!isGenericSport && (
        <DisplayStandingsModal
          isOpen={isGoalieStatsModalOpen}
          onClose={() => setIsGoalieStatsModalOpen(false)}
          onSave={(selectedStats) => {
            setDisplayedGoalieStats(selectedStats)
            if (
              goalieStatsDefaultSortBy !== null &&
              !selectedStats.includes(goalieStatsDefaultSortBy)
            ) {
              setGoalieStatsDefaultSortBy(null)
            }
            setIsGoalieStatsModalOpen(false)
          }}
          currentDisplayed={displayedGoalieStats}
          statTypes={playerStatsTypes}
          defaultSortBy={null}
          title={
            isSoccer
              ? 'Customize Displayed Keeper Statistics'
              : 'Customize Displayed Goalie Statistics'
          }
        />
      )}
    </div>
  )
}

export default SettingsForm
